import type { ReactNode } from "react";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export interface PluginStatus {
  state: "active" | "success" | "warning" | "error" | "inactive" | "loading";
  badge?: string | number;
  summary?: string;
}

export interface DevToolsPlugin {
  id: string;
  name: string;
  icon: IconDefinition;
  render: () => ReactNode;
  useStatus?: () => PluginStatus;
}

export interface DevToolsState {
  isDrawerOpen: boolean;
  isSidebarExpanded: boolean;
  activePluginId: string | null;
  drawerWidth: number;
}

export interface DevToolsContextValue extends DevToolsState {
  plugins: DevToolsPlugin[];
  setActivePlugin: (id: string | null) => void;
  toggleDrawer: () => void;
  toggleSidebar: () => void;
}

const STORAGE_KEY = "chopin-devtools-state";

export const DEFAULT_STATE: DevToolsState = {
  isDrawerOpen: false,
  isSidebarExpanded: false,
  activePluginId: null,
  drawerWidth: 320,
};

export function loadPersistedState(): DevToolsState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      // Drawer is ephemeral; never restore as open. Sidebar expansion persists.
      return { ...DEFAULT_STATE, ...parsed, isDrawerOpen: false };
    }
  } catch {
    // ignore corrupt storage
  }
  return DEFAULT_STATE;
}

export function persistState(state: DevToolsState): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore storage errors
  }
}
